import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {  tapResponse } from '@ngrx/operators';
import { EMPTY, forkJoin, pipe, withLatestFrom } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { RetailItemSale } from '../../models/retail-item-sale';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { WorkshopJob } from '../../models/workshop-job';
import { WorkshopJobRetailItemSaleService } from '../../shared/services/workshop-job-retail-item-sale.service';
import { WorkshopJobService } from '../../shared/services/workshop-job.service';
import { CustomerEquipment } from '../../models/customer-equipment';

export interface WorkshopJobListState {
  docketId: number;
  items: WorkshopJob<CustomerEquipment>[];
  jobPartsUsed: Record<number, RetailItemSale[]>; // key is workshop job id
  includeDeleted: boolean;
  loading: boolean;
  error: string | null;
  adding: boolean;
}

const defaultState: WorkshopJobListState = {
  docketId: null,
  items: [],
  jobPartsUsed: {},
  includeDeleted: false,
  loading: true,
  error: null,
  adding: false
};

@Injectable()
export class WorkshopJobListStore extends ComponentStore<WorkshopJobListState> {
  constructor(
    private workshopJobService: WorkshopJobService,
    private retailItemService: WorkshopJobRetailItemSaleService,
    private dialog: MatDialog,
    private toastrService: ToastrService
  ) {
    super(defaultState);
  }

  readonly items$ = this.select((state) => state.items);
  readonly itemsWithParts$ = this.select((state) =>
    state.items.map((item) => ({
      ...item,
      total_cost: this.getTotalCost(item, state.jobPartsUsed[item.id] ?? []),
      parts: state.jobPartsUsed[item.id] ?? []
    }))
  );

  readonly loading$ = this.select((state) => state.loading);

  readonly setDocketId = this.effect<number>(
    pipe(
      filter(Boolean),
      tap((id) => this.patchState({ loading: true, docketId: id })),
      switchMap((id) => {
        return this.workshopJobService.getForRepairDocket(id).pipe(
          tapResponse(
            (items) => {
              this.patchState({ docketId: id, items });
            },
            (error: Error) =>
              this.patchState({
                error: error.message ?? 'Unable to get workshop jobs'
              }),
            () => this.patchState({ loading: false })
          ),
          mergeMap((items) => {
            // map each job to a request for the parts used in it
            const partsUsed$ = items.map(({ id }) =>
              this.retailItemService.getSaleItemsForSellable(id).pipe(
                map((partsUsed) => ({
                  id,
                  partsUsed
                }))
              )
            );
            // combine the response of all requests to set the parts used state property
            return forkJoin(partsUsed$).pipe(
              map((partsUsed) =>
                partsUsed.reduce((acc, { id, partsUsed }) => {
                  acc[id] = partsUsed;
                  return acc;
                }, {})
              )
            );
          }),
          tapResponse(
            (partsUsed) => this.patchState({ jobPartsUsed: partsUsed }),
            (error: Error) =>
              this.toastrService.error(
                error.message ?? 'Unable to get parts used for workshop jobs'
              )
          )
        );
      })
    )
  );

  readonly delete = this.effect<number>(
    pipe(
      withLatestFrom(
        this.select((state) => state.docketId),
        this.select((state) => state.items)
      ),
      switchMap(([id, docketId, items]) => {
        return this.workshopJobService.delete(id).pipe(
          tapResponse(
            () => {
              this.removeItem(id);
            },
            (error: Error) =>
              this.patchState({
                error: error.message ?? 'Unable to delete sales item'
              })
          )
        );
      })
    )
  );

  readonly update = this.effect<{
    id: number;
    update: Partial<WorkshopJob<CustomerEquipment>>;
    onSuccess?: () => void;
  }>(
    pipe(
      withLatestFrom(
        this.select((state) => state.docketId),
        this.select((state) => state.items)
      ),
      switchMap(([{ id, update, onSuccess }, docketId, items]) => {
        const withIntCost = {
          ...update,
          labour_cost: update.labour_cost
            ? update.labour_cost * 100
            : update.labour_cost
        };
        return this.workshopJobService.update(id, withIntCost).pipe(
          tapResponse(
            () => {
              this.patchState({
                items: items.map((i) =>
                  i.id === id ? { ...i, ...withIntCost } : i
                )
              });
              onSuccess && onSuccess();
            },
            (error: Error) =>
              this.toastrService.error(
                error.message ?? 'Unable to update workshop job',
                'Error'
              )
          )
        );
      })
    )
  );

  private readonly appendItem = this.updater(
    (state, item: WorkshopJob<CustomerEquipment>) => ({
      ...state,
      items: [...state.items, item]
    })
  );

  private readonly removeItem = this.updater((state, id: number) => ({
    ...state,
    items: state.items.filter((item) => item.id !== id)
  }));

  readonly addWorkshopJob = this.effect<{
    item: Partial<WorkshopJob<CustomerEquipment>>;
    onSuccess?: () => void;
  }>(
    pipe(
      tap(() => this.patchState({ adding: true })),
      mergeMap(({ item, onSuccess }) =>
        this.select((state) => state.docketId).pipe(
          filter(Boolean),
          take(1),
          map<number, [Partial<WorkshopJob<CustomerEquipment>>, number]>(
            (docketId) => [item, docketId]
          ),
          switchMap(([item, docketId]) => {
            return this.workshopJobService
              .addWorkshopJob({ ...item, repair_docket_id: docketId })
              .pipe(catchError((res) => this.handleAddError(res, 'add', item)));
          }),
          tapResponse(
            (item: WorkshopJob<CustomerEquipment>) => this.appendItem(item),
            (error: Error) => console.log(error)
          ),
          tap(() => onSuccess && onSuccess()),
          tap(() => this.patchState({ adding: false }))
        )
      )
    )
  );

  private handleAddError(
    res: HttpErrorResponse,
    method: 'quickAdd' | 'add',
    params?: any
  ) {
    return EMPTY;
  }

  private getTotalCost(
    item: WorkshopJob<CustomerEquipment>,
    retailItemSales: RetailItemSale[]
  ) {
    return (
      (retailItemSales.reduce(
        (acc, { unit_price, quantity, tax_rate }) =>
          acc + unit_price * quantity * (1 + tax_rate),
        0
      ) +
        item.labour_cost * (1 + item.tax_rate)) /
      100
    );
  }
}
